import React, { useCallback, useEffect, useRef, useState } from "react";
import { API, apiError } from "@/lib/api";
import { Wrench, RefreshCw, PackageSearch, Download, CheckCircle2, AlertTriangle } from "lucide-react";

function eur(cents) {
  if (cents === null || cents === undefined) return "—";
  return (cents / 100).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}

function goodsValueText(s) {
  if (!s.delivered) return "—";
  if (!s.deliveredGoodsKnown) return `— (sconosciuto per tutte le ${s.delivered})`;
  const unk = s.deliveredGoodsUnknown > 0 ? `, sconosciuto per ${s.deliveredGoodsUnknown}` : "";
  return `${eur(s.deliveredGoodsCents)} — noto per ${s.deliveredGoodsKnown}${unk}`;
}

function toSummary(s) {
  return {
    scopeTotal: s.scope.total,
    withEvents: s.scope.with_events,
    delivered: s.scope.delivered,
    deliveredCost: s.scope.delivered_cost_cents,
    deliveredGoodsCents: s.scope.delivered_goods_cents,
    deliveredGoodsKnown: s.scope.delivered_goods_known,
    deliveredGoodsUnknown: s.scope.delivered_goods_unknown,
    returned: s.scope.returned,
    stillMute: s.scope.still_mute,
    muteReasons: s.scope.mute_reasons,
    codPaid: s.cod_paid,
  };
}

function ageText(seconds) {
  if (seconds === null || seconds === undefined) return "mai";
  if (seconds < 90) return `${seconds} secondi fa`;
  const m = Math.round(seconds / 60);
  if (m < 90) return `${m} minuti fa`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} ore fa`;
  return `${Math.round(h / 24)} giorni fa`;
}

function BigRow({ label, value, tone = "default", testid }) {
  const tones = {
    default: "text-slate-900",
    ok: "text-emerald-700",
    warn: "text-amber-700",
    bad: "text-rose-700",
  };
  return (
    <div className="flex items-baseline justify-between gap-6 border-b border-slate-100 py-2.5 last:border-0" data-testid={testid}>
      <span className="text-base text-slate-600">{label}</span>
      <span className={`text-2xl font-extrabold tabular-nums ${tones[tone]}`}>{value}</span>
    </div>
  );
}

function HealthBox({ status, onRefresh, loading }) {
  if (!status) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="health-box">
        <p className="text-base text-slate-500">Lettura dello stato in corso…</p>
      </div>
    );
  }
  const sweeperAlive = status.last_poll_age_seconds !== null && status.last_poll_age_seconds < 180;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="health-box">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-extrabold text-slate-900">Stato di salute del recupero</h2>
        <button
          data-testid="health-refresh-btn"
          onClick={onRefresh}
          disabled={loading}
          className="flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-base font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          <RefreshCw size={18} className={loading ? "animate-spin" : ""} /> Aggiorna
        </button>
      </div>
      <BigRow label="Spedizioni attive" value={status.active_total} testid="health-active" />
      <BigRow
        label="In coda di lettura dallo sweeper"
        value={status.in_coda}
        tone={status.in_coda > 0 ? "warn" : "ok"}
        testid="health-in-coda"
      />
      <BigRow
        label="Con tracking ma zero eventi"
        value={status.mute_total}
        tone={status.mute_total > 0 ? "warn" : "ok"}
        testid="health-mute"
      />
      <BigRow
        label="Chiuse e saldate (non vanno più seguite)"
        value={status.closed_terminal}
        testid="health-closed"
      />
      <BigRow
        label="Il negozio collegato non esiste più"
        value={status.orphan_store}
        tone={status.orphan_store > 0 ? "bad" : "ok"}
        testid="health-orphan"
      />
      <BigRow
        label="Il corriere non ha pubblicato eventi (verificato)"
        value={status.carrier_no_events}
        testid="health-no-events"
      />
      <BigRow
        label="Interrogazioni corriere fallite"
        value={status.failed_checks}
        tone={status.failed_checks > 0 ? "bad" : "ok"}
        testid="health-failed"
      />
      <BigRow
        label="Consegnate in contrassegno, non ancora versate dal corriere"
        value={
          status.cod_owed
            ? `${status.cod_owed.count} (${eur(status.cod_owed.amount_cents)}${
                status.cod_owed.unknown_amount > 0 ? ` — di cui ${status.cod_owed.unknown_amount} senza importo` : ""
              })`
            : "—"
        }
        tone={status.cod_owed && status.cod_owed.count > 0 ? "warn" : "ok"}
        testid="health-cod-owed"
      />
      <BigRow
        label="Ultimo controllo dello sweeper"
        value={ageText(status.last_poll_age_seconds)}
        tone={sweeperAlive ? "ok" : "bad"}
        testid="health-last-poll"
      />
      <BigRow label="La più vecchia in attesa da" value={ageText(status.oldest_poll_age_seconds)} testid="health-oldest-poll" />
      <p className="mt-4 text-base leading-snug" data-testid="health-sweeper-verdict">
        {sweeperAlive ? (
          <span className="font-bold text-emerald-700">Lo sweeper sta girando regolarmente.</span>
        ) : (
          <span className="font-bold text-rose-700">
            Lo sweeper NON risulta attivo negli ultimi 3 minuti: il recupero automatico è fermo, segnalalo prima di ripubblicare di nuovo.
          </span>
        )}
      </p>
    </div>
  );
}

function ConfirmButton({ onConfirm, disabled, busy, label, busyLabel, testid, confirmText }) {
  return (
    <button
      data-testid={testid}
      disabled={disabled || busy}
      onClick={() => {
        if (window.confirm(confirmText)) onConfirm();
      }}
      className="flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 text-lg font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {busy ? busyLabel : label}
    </button>
  );
}

export default function MaintenancePage() {
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [pageError, setPageError] = useState(null);

  const [since, setSince] = useState("2026-08-26");
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillProgress, setBackfillProgress] = useState(null);
  const [backfillSummary, setBackfillSummary] = useState(null);
  const [backfillError, setBackfillError] = useState(null);

  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState("ALL");
  const [importBusy, setImportBusy] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const [importSummary, setImportSummary] = useState(null);
  const [importError, setImportError] = useState(null);
  const [importSince, setImportSince] = useState("2026-08-26");
  const [importPreview, setImportPreview] = useState(null);

  const [undoBusy, setUndoBusy] = useState(false);
  const [undoResult, setUndoResult] = useState(null);
  const [undoError, setUndoError] = useState(null);

  const pollRef = useRef(null);

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const r = await API.get("/maintenance/status", { params: { since } });
      setStatus(r.data);
      return r.data;
    } catch (e) {
      setPageError(apiError(e));
      return null;
    } finally {
      setStatusLoading(false);
    }
  }, [since]);

  useEffect(() => {
    loadStatus();
    API.get("/stores").then((r) => setStores(r.data.stores || r.data || [])).catch(() => {});
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startPolling = useCallback(
    (onTick) => {
      clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        const s = await loadStatus();
        if (s) onTick(s);
      }, 5000);
    },
    [loadStatus]
  );
  const stopPolling = () => clearInterval(pollRef.current);

  const runBackfill = async () => {
    setBackfillBusy(true);
    setBackfillError(null);
    setBackfillSummary(null);
    let queued = 0;
    try {
      const r = await API.post("/shipments/backfill-tracking", { since });
      queued = r.data.queued;
    } catch (e) {
      setBackfillError(apiError(e));
      setBackfillBusy(false);
      return;
    }
    let lastMute = null;
    let stableRounds = 0;
    setBackfillProgress({ queued, remaining: queued, round: 0 });
    startPolling((s) => {
      const remaining = s.scope.still_mute;
      setBackfillProgress({ queued, remaining, round: (lastMute === null ? 0 : 1) });
      if (lastMute !== null && remaining >= lastMute) stableRounds += 1;
      else stableRounds = 0;
      lastMute = remaining;
      if (remaining === 0 || stableRounds >= 4) {
        stopPolling();
        setBackfillSummary(toSummary(s));
        setBackfillBusy(false);
      }
    });
    // fermo di sicurezza: dopo 10 minuti smetto di attendere e mostro comunque il riepilogo
    setTimeout(async () => {
      if (!pollRef.current) return;
      stopPolling();
      const s = await loadStatus();
      if (s) {
        setBackfillSummary(toSummary(s));
      }
      setBackfillBusy(false);
    }, 600000);
  };

  const runImport = async (dryRun) => {
    setImportBusy(true);
    setImportError(null);
    if (dryRun) setImportPreview(null);
    else setImportSummary(null);
    const targets = storeId === "ALL" ? stores : stores.filter((s) => (s.id || s._id) === storeId);
    if (targets.length === 0) {
      setImportError("Nessuno store selezionato.");
      setImportBusy(false);
      return;
    }
    const results = [];
    for (let i = 0; i < targets.length; i += 1) {
      const st = targets[i];
      setImportProgress({ current: i + 1, total: targets.length, storeName: st.name });
      try {
        const r = await API.post("/shipments/import-from-spediamo", {
          store_id: st.id || st._id,
          max_pages: 50,
          since: importSince,
          dry_run: dryRun,
        });
        results.push({ store: st.name, ...r.data, error: null });
      } catch (e) {
        results.push({ store: st.name, imported: 0, skipped_existing: 0, in_period: 0, would_import: 0, pages_read: 0, error: apiError(e) });
      }
      await loadStatus();
    }
    if (dryRun) {
      setImportPreview(results);
    } else {
      const s = await loadStatus();
      setImportSummary({ results, imported: s ? s.imported : null });
      setImportPreview(null);
    }
    setImportProgress(null);
    setImportBusy(false);
  };

  const previewTotals = importPreview
    ? importPreview.reduce((acc, r) => ({ would: acc.would + (r.would_import || 0), present: acc.present + (r.already_present || 0) }), { would: 0, present: 0 })
    : null;

  const runUndoImport = async () => {
    setUndoBusy(true);
    setUndoError(null);
    setUndoResult(null);
    try {
      const pre = await API.post("/shipments/drop-imported", { dry_run: true });
      const d = pre.data;
      const kept =
        d.kept_linked_to_hub > 0
          ? ` ${d.kept_linked_to_hub} resteranno in archivio perché collegate a ordini lavorati dall'hub.`
          : "";
      const ok = window.confirm(
        `Sei sicuro? Verranno rimosse definitivamente ${d.removable} spedizioni importate da Spediamo Pro.${kept} Le spedizioni nate dall'hub NON vengono toccate.`
      );
      if (!ok) {
        setUndoBusy(false);
        return;
      }
      const r = await API.post("/shipments/drop-imported", { dry_run: false });
      setUndoResult(r.data);
      await loadStatus();
    } catch (e) {
      setUndoError(apiError(e));
    }
    setUndoBusy(false);
  };

  return (
    <div className="mx-auto max-w-4xl px-6 py-8" data-testid="maintenance-page">
      <h1 className="flex items-center gap-3 text-3xl font-extrabold text-slate-900">
        <Wrench size={28} /> Manutenzione — Recupero dati
      </h1>
      <p className="mt-2 max-w-2xl text-base leading-snug text-slate-600">
        Operazioni di recupero dello storico spedizioni. Ogni azione chiede conferma e mostra l'avanzamento a video.
      </p>
      {pageError && (
        <div className="mt-4 rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-base font-semibold text-rose-800" data-testid="maintenance-page-error">
          {pageError}
        </div>
      )}

      <div className="mt-6">
        <HealthBox status={status} loading={statusLoading} onRefresh={loadStatus} />
      </div>

      {/* ---------------- Recupero tracking ---------------- */}
      <div className="mt-8 rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="backfill-card">
        <h2 className="flex items-center gap-2 text-xl font-extrabold text-slate-900">
          <PackageSearch size={22} /> Recupera tracking mancanti
        </h2>
        <p className="mt-1 max-w-xl text-base leading-snug text-slate-600">
          Rilegge dal corriere tutte le spedizioni attive con numero di tracking, a partire dalla data scelta.
          Lo sweeper le processa a gruppi da 50 circa ogni 45 secondi.
        </p>
        <div className="mt-4 flex flex-wrap items-end gap-4">
          <label className="block">
            <span className="mb-1 block text-base font-bold text-slate-700">Recupera dalla data</span>
            <input
              data-testid="backfill-since-input"
              type="date"
              value={since}
              onChange={(e) => setSince(e.target.value)}
              disabled={backfillBusy}
              className="h-12 rounded-lg border border-slate-300 px-3 text-lg outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
          </label>
          <ConfirmButton
            testid="backfill-start-btn"
            label="Recupera tracking mancanti"
            busyLabel="Recupero in corso…"
            busy={backfillBusy}
            disabled={!since}
            confirmText={`Sei sicuro? Verranno rilette dal corriere tutte le spedizioni attive con tracking create dal ${since}.`}
            onConfirm={runBackfill}
          />
        </div>
        {backfillProgress && backfillBusy && (
          <div className="mt-4 rounded-lg bg-blue-50 px-4 py-3 text-lg font-semibold text-blue-900" data-testid="backfill-progress">
            In coda: {backfillProgress.queued} spedizioni — ancora mute: {backfillProgress.remaining}. La pagina si aggiorna da sola ogni 5 secondi.
          </div>
        )}
        {backfillError && (
          <div className="mt-4 rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-base font-semibold text-rose-800" data-testid="backfill-error">
            Operazione fallita: {backfillError}
          </div>
        )}
        {backfillSummary && (
          <div className="mt-4 rounded-lg border border-emerald-300 bg-emerald-50 p-5" data-testid="backfill-summary">
            <h3 className="flex items-center gap-2 text-lg font-extrabold text-emerald-900">
              <CheckCircle2 size={20} /> Recupero completato (dal {since})
            </h3>
            <div className="mt-2">
              <BigRow label="Spedizioni nel perimetro" value={backfillSummary.scopeTotal} testid="bf-total" />
              <BigRow label="Ora con eventi di tracking" value={backfillSummary.withEvents} tone="ok" testid="bf-with-events" />
              <BigRow label="Consegnate" value={backfillSummary.delivered} tone="ok" testid="bf-delivered" />
              <BigRow
                label="Valore merce delle consegnate"
                value={goodsValueText(backfillSummary)}
                tone="ok"
                testid="bf-delivered-goods"
              />
              <BigRow
                label="Costo corriere delle consegnate"
                value={eur(backfillSummary.deliveredCost)}
                testid="bf-delivered-cost"
              />
              <BigRow label="Rientrate al mittente" value={backfillSummary.returned} tone={backfillSummary.returned > 0 ? "warn" : "default"} testid="bf-returned" />
              <BigRow label="Ancora mute" value={backfillSummary.stillMute} tone={backfillSummary.stillMute > 0 ? "bad" : "ok"} testid="bf-still-mute" />
              <BigRow
                label="Contrassegni PAGATI — importo riscosso dal corriere"
                value={`${backfillSummary.codPaid.count} (${eur(backfillSummary.codPaid.amount_cents)})`}
                tone="ok"
                testid="bf-cod-paid"
              />
            </div>
            {backfillSummary.muteReasons.length > 0 && (
              <div className="mt-3" data-testid="bf-mute-reasons">
                <p className="text-base font-bold text-rose-800">Spedizioni ancora mute — motivo:</p>
                <ul className="mt-1 space-y-1">
                  {backfillSummary.muteReasons.map((m) => (
                    <li key={m.order} className="text-base leading-snug text-rose-800">
                      <span className="font-mono font-bold">{m.order}</span> — {m.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ---------------- Import da Spediamo Pro ---------------- */}
      <div className="mt-8 rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="import-card">
        <h2 className="flex items-center gap-2 text-xl font-extrabold text-slate-900">
          <Download size={22} /> Importa da Spediamo Pro
        </h2>
        <p className="mt-1 max-w-xl text-base leading-snug text-slate-600">
          Importa le spedizioni create direttamente su Spediamo Pro (mai passate dall'hub), a partire dalla data scelta.
          Quelle già presenti vengono saltate. Prima si contano senza scrivere nulla, poi si conferma.
        </p>
        <div className="mt-4 flex flex-wrap items-end gap-4">
          <label className="block">
            <span className="mb-1 block text-base font-bold text-slate-700">Store</span>
            <select
              data-testid="import-store-select"
              value={storeId}
              onChange={(e) => setStoreId(e.target.value)}
              disabled={importBusy}
              className="h-12 min-w-64 rounded-lg border border-slate-300 px-3 text-lg outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="ALL">Tutti gli store</option>
              {stores.map((s) => (
                <option key={s.id || s._id} value={s.id || s._id}>
                  {s.name} ({s.code})
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-base font-bold text-slate-700">Importa dal</span>
            <input
              data-testid="import-since-input"
              type="date"
              value={importSince}
              onChange={(e) => {
                setImportSince(e.target.value);
                setImportPreview(null);
              }}
              disabled={importBusy}
              className="h-12 rounded-lg border border-slate-300 px-3 text-lg outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
          </label>
          <ConfirmButton
            testid="import-count-btn"
            label="Conta"
            busyLabel="Conteggio in corso…"
            busy={importBusy}
            disabled={stores.length === 0 || !importSince}
            confirmText={`Contare le spedizioni Spediamo Pro dal ${importSince}? Nessuna scrittura: è solo un conteggio.`}
            onConfirm={() => runImport(true)}
          />
          <ConfirmButton
            testid="import-start-btn"
            label="Conferma e importa"
            busyLabel="Importazione in corso…"
            busy={importBusy}
            disabled={stores.length === 0 || !importPreview || !previewTotals || previewTotals.would === 0}
            confirmText={
              previewTotals
                ? `Sei sicuro? Verranno importate ${previewTotals.would} spedizioni dal ${importSince} (${previewTotals.present} già presenti verranno saltate).`
                : "Prima esegui il conteggio."
            }
            onConfirm={() => runImport(false)}
          />
        </div>
        {importPreview && (
          <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3" data-testid="import-preview">
            <p className="text-lg font-bold text-amber-900">Anteprima — nessuna scrittura eseguita:</p>
            <ul className="mt-1 space-y-1">
              {importPreview.map((r) => (
                <li key={r.store} className="text-base leading-snug text-amber-900" data-testid={`import-preview-${r.store}`}>
                  <strong>{r.store}</strong>:{" "}
                  {r.error ? (
                    <span className="font-semibold text-rose-700">lettura fallita — {r.error}</span>
                  ) : (
                    <span>
                      nel periodo dal {importSince}: <strong>{r.in_period}</strong> spedizioni, già presenti{" "}
                      <strong>{r.already_present}</strong>, di altri store <strong>{r.other_store || 0}</strong>, non
                      attribuibili <strong>{(r.unattributed || 0) + (r.ambiguous || 0)}</strong>,{" "}
                      <strong>ne verrebbero importate {r.would_import}</strong> (lette {r.pages_read} pagine su{" "}
                      {r.total_pages || "?"})
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        {importProgress && (
          <div className="mt-4 rounded-lg bg-blue-50 px-4 py-3 text-lg font-semibold text-blue-900" data-testid="import-progress">
            Store {importProgress.current} di {importProgress.total}: <strong>{importProgress.storeName}</strong> — lettura da Spediamo Pro in corso…
          </div>
        )}
        {importError && (
          <div className="mt-4 rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-base font-semibold text-rose-800" data-testid="import-error">
            {importError}
          </div>
        )}
        {importSummary && (
          <div className="mt-4 rounded-lg border border-emerald-300 bg-emerald-50 p-5" data-testid="import-summary">
            <h3 className="flex items-center gap-2 text-lg font-extrabold text-emerald-900">
              <CheckCircle2 size={20} /> Importazione completata
            </h3>
            <ul className="mt-2 space-y-1">
              {importSummary.results.map((r) => (
                <li key={r.store} className="text-lg leading-snug" data-testid={`import-row-${r.store}`}>
                  <strong>{r.store}</strong>:{" "}
                  {r.error ? (
                    <span className="font-semibold text-rose-700">fallita — {r.error}</span>
                  ) : (
                    <span>
                      importate <strong>{r.imported}</strong>, già presenti (saltate) <strong>{r.skipped_existing}</strong>
                    </span>
                  )}
                </li>
              ))}
            </ul>
            {importSummary.imported && (
              <div className="mt-3 border-t border-emerald-200 pt-3">
                <BigRow label="Totale importate (storico)" value={importSummary.imported.total} testid="imp-total" />
                <BigRow label="Di cui consegnate" value={importSummary.imported.delivered} tone="ok" testid="imp-delivered" />
                <BigRow
                  label="Agganciate a un ordine Shopify"
                  value={importSummary.imported.linked_to_shopify}
                  tone={importSummary.imported.linked_to_shopify > 0 ? "ok" : "warn"}
                  testid="imp-linked"
                />
                <BigRow
                  label="Senza riferimento ordine (orfane)"
                  value={importSummary.imported.orphan}
                  tone={importSummary.imported.orphan > 0 ? "warn" : "ok"}
                  testid="imp-orphan"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* ---------------- Annulla importazione ---------------- */}
      <div className="mt-8 rounded-xl border border-rose-200 bg-white p-6 shadow-sm" data-testid="undo-import-card">
        <h2 className="flex items-center gap-2 text-xl font-extrabold text-slate-900">
          <AlertTriangle size={22} className="text-rose-600" /> Annulla importazione
        </h2>
        <p className="mt-1 max-w-xl text-base leading-snug text-slate-600">
          Rimuove in blocco SOLO le spedizioni con marcatore "importata da Spediamo Pro". Le spedizioni nate dall'hub non
          vengono toccate; quelle importate ma collegate a un ordine lavorato dall'hub restano e vengono contate a parte.
        </p>
        <p className="mt-3 text-lg font-bold text-slate-900" data-testid="undo-import-count">
          Attualmente in archivio: {status ? status.imported.total : "—"} spedizioni importate.
        </p>
        <div className="mt-4">
          <button
            data-testid="undo-import-btn"
            onClick={runUndoImport}
            disabled={undoBusy || !status || status.imported.total === 0}
            className="flex items-center gap-2 rounded-lg bg-rose-600 px-6 py-3 text-lg font-bold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {undoBusy ? "Rimozione in corso…" : "Annulla import"}
          </button>
        </div>
        {undoError && (
          <div className="mt-4 rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-base font-semibold text-rose-800" data-testid="undo-import-error">
            Operazione fallita: {undoError}
          </div>
        )}
        {undoResult && (
          <div className="mt-4 rounded-lg border border-emerald-300 bg-emerald-50 p-5" data-testid="undo-import-result">
            <h3 className="flex items-center gap-2 text-lg font-extrabold text-emerald-900">
              <CheckCircle2 size={20} /> Importazione annullata
            </h3>
            <BigRow label="Spedizioni importate rimosse" value={undoResult.removed} tone="ok" testid="undo-removed" />
            <BigRow
              label="Tenute in archivio (collegate all'hub)"
              value={undoResult.kept_linked_to_hub}
              tone={undoResult.kept_linked_to_hub > 0 ? "warn" : "default"}
              testid="undo-kept"
            />
            <BigRow label="Spedizioni attive rimaste" value={undoResult.active_total} testid="undo-active-total" />
          </div>
        )}
      </div>

      <div className="mt-6 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" data-testid="maintenance-note">
        <AlertTriangle size={20} className="mt-0.5 shrink-0 text-amber-600" />
        <p className="text-base leading-snug text-amber-900">
          Gli eventi di consegna e lo stato del contrassegno delle spedizioni importate si completano da soli al primo giro
          dello sweeper (pochi minuti). Se dopo 10 minuti il quadro qui sopra non si muove, segnalalo prima di ritentare.
        </p>
      </div>
    </div>
  );
}
